import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, apiJson } from "../../utils/api.js";
import { getChatPath } from "../../utils/nav.js";
import { fmtDt, reportDefaultDateRange, userTitle } from "../../utils/users.js";
import { useMe } from "../../hooks/useMe.js";
import { CrmRow } from "../components/CrmRow.jsx";
import { PageReveal } from "../components/PageReveal.jsx";

const REPORT_TABS = ["stats", "transitions", "calls"];

function fmtDayShort(yyyyMmDd) {
  const s = String(yyyyMmDd || "");
  // 2026-03-06 -> 06.03
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s.slice(8, 10)}.${s.slice(5, 7)}`;
  return s;
}

function sumStageDates(stage) {
  const dates = stage?.dates || {};
  return Object.values(dates).reduce((acc, v) => acc + (Number(v) || 0), 0);
}

function defaultReportDateRange() {
  return reportDefaultDateRange();
}

const SUMMARY_KINDS = {
  total_clients: { title: "Клиенты", subtitle: "все клиенты в базе" },
  selection: { title: "Подборы", subtitle: "сделки в воронке «Подбор»" },
  prepayment: { title: "Предоплаты", subtitle: "этап «Предоплата получена»" },
  refusal: { title: "Отказы", subtitle: "воронка «База отказов»" },
};

export function ReportsPage() {
  const { me, loading, canViewReports } = useMe();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo(() => {
    const t = searchParams.get("tab") || "";
    if (t === "audit") return "stats";
    return REPORT_TABS.includes(t) ? t : "stats";
  }, [searchParams]);

  const setTab = useCallback(
    (t) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (REPORT_TABS.includes(t)) p.set("tab", t);
          else p.delete("tab");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const defaultRange = useMemo(() => defaultReportDateRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);
  const [funnelId, setFunnelId] = useState("sales");

  const [stats, setStats] = useState(null);
  const [transitions, setTransitions] = useState({ period: "", funnel: "", stages: [], dateKeys: [] });
  const [expandedTransitionStages, setExpandedTransitionStages] = useState(() => new Set());
  const [calls, setCalls] = useState([]);
  const [callsTotals, setCallsTotals] = useState(null);
  const [callsPeriod, setCallsPeriod] = useState("");
  const [funnels, setFunnels] = useState([]);

  const [loadingReports, setLoadingReports] = useState(false);

  const [summaryModalKind, setSummaryModalKind] = useState(null);
  const [summaryModalItems, setSummaryModalItems] = useState([]);
  const [summaryModalTotal, setSummaryModalTotal] = useState(0);
  const [summaryModalOffset, setSummaryModalOffset] = useState(0);
  const [summaryModalLoading, setSummaryModalLoading] = useState(false);
  const SUMMARY_PAGE = 50;

  const openSummaryModal = useCallback(async (kind) => {
    setSummaryModalKind(kind);
    setSummaryModalItems([]);
    setSummaryModalOffset(0);
    setSummaryModalTotal(0);
    setSummaryModalLoading(true);
    try {
      const data = await apiJson(
        `/api/reports/summary-detail?kind=${encodeURIComponent(kind)}&limit=${SUMMARY_PAGE}&offset=0`
      );
      setSummaryModalItems(data.items ?? []);
      setSummaryModalTotal(Number(data.total) || 0);
      setSummaryModalOffset((data.items ?? []).length);
    } catch {
      setSummaryModalItems([]);
      setSummaryModalTotal(0);
    } finally {
      setSummaryModalLoading(false);
    }
  }, []);

  const loadMoreSummaryModal = useCallback(async () => {
    if (!summaryModalKind || summaryModalLoading) return;
    if (summaryModalOffset >= summaryModalTotal) return;
    setSummaryModalLoading(true);
    try {
      const data = await apiJson(
        `/api/reports/summary-detail?kind=${encodeURIComponent(summaryModalKind)}&limit=${SUMMARY_PAGE}&offset=${summaryModalOffset}`
      );
      const next = data.items ?? [];
      setSummaryModalItems((prev) => [...prev, ...next]);
      setSummaryModalOffset((o) => o + next.length);
    } finally {
      setSummaryModalLoading(false);
    }
  }, [summaryModalKind, summaryModalLoading, summaryModalOffset, summaryModalTotal]);

  const closeSummaryModal = useCallback(() => {
    setSummaryModalKind(null);
    setSummaryModalItems([]);
    setSummaryModalTotal(0);
    setSummaryModalOffset(0);
  }, []);

  const loadReports = useCallback(async () => {
    const qs = new URLSearchParams();
    qs.set("date_from", dateFrom);
    qs.set("date_to", dateTo);
    qs.set("funnel_id", funnelId);
    qs.set("include_audit", "false");
    setLoadingReports(true);
    try {
      const data = await apiJson(`/api/reports?${qs.toString()}`);
      setStats(data.stats ?? null);
      setTransitions(data.transitions ?? { period: "", funnel: "", stages: [], dateKeys: [] });
      setCalls(data.calls ?? []);
      setCallsTotals(data.calls_totals ?? null);
      setCallsPeriod(typeof data.calls_period === "string" ? data.calls_period : "");
      setFunnels(data.funnels ?? []);
    } finally {
      setLoadingReports(false);
    }
  }, [dateFrom, dateTo, funnelId]);

  const [downloadingReport, setDownloadingReport] = useState(null);
  const downloadReport = useCallback(
    async (reportType) => {
      const qs = new URLSearchParams();
      qs.set("date_from", dateFrom);
      qs.set("date_to", dateTo);
      if (reportType === "transitions") qs.set("funnel_id", funnelId);
      setDownloadingReport(reportType);
      try {
        const res = await apiFetch(`/api/reports/export/${reportType}?${qs.toString()}`);
        if (!res.ok) throw new Error(await res.text());
        const blob = await res.blob();
        const name = res.headers.get("Content-Disposition")?.match(/filename="?([^";]+)"?/)?.[1] || `report_${reportType}.xlsx`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        setDownloadingReport(null);
      }
    },
    [dateFrom, dateTo, funnelId]
  );

  useEffect(() => {
    if (!canViewReports) return;
    let cancelled = false;
    (async () => {
      try {
        await loadReports();
      } catch {
        if (!cancelled) setStats(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canViewReports, loadReports, dateFrom, dateTo, funnelId]);

  useEffect(() => {
    setExpandedTransitionStages(new Set());
  }, [dateFrom, dateTo, funnelId]);

  if (me && !canViewReports) return <Navigate to="/deals" replace />;

  const ready = !loading && !!me;

  const summary = stats?.summary ?? {};

  const byManagerRows = useMemo(() => {
    const rows = (stats?.by_manager || []).map((m) => ({
      id: m.id,
      name: (m.display_name || m.code || "—").trim() || "—",
      total: Number(m.total) || 0,
      newCount: Number(m.new_count) || 0,
    }));
    rows.sort((a, b) => b.total - a.total || b.newCount - a.newCount);
    return rows;
  }, [stats]);

  const callsTotalsResolved = useMemo(() => {
    const list = calls || [];
    const fromList = () => {
      if (!list.length) return null;
      const sumC = list.reduce((a, m) => a + (Number(m.callsCount) || 0), 0);
      const dealIds = new Set();
      for (const m of list) {
        for (const d of m.byDeal || []) {
          if (d.dealId != null && d.dealId !== undefined) dealIds.add(d.dealId);
        }
      }
      return { calls_count: sumC, deals_count: dealIds.size };
    };
    if (callsTotals && typeof callsTotals.calls_count === "number") {
      const merged = fromList();
      return {
        calls_count: callsTotals.calls_count,
        deals_count:
          typeof callsTotals.deals_count === "number"
            ? callsTotals.deals_count
            : merged?.deals_count ?? 0,
      };
    }
    return fromList();
  }, [callsTotals, calls]);

  const transitionsTable = useMemo(() => {
    const dateKeys = transitions?.dateKeys || [];
    const stages = transitions?.stages || [];
    const rows = stages
      .map((s) => ({
        stageId: s.stageId,
        stageTitle: s.stageTitle || s.stageId,
        total: sumStageDates(s),
        dates: s.dates || {},
        byManager: Array.isArray(s.byManager) ? s.byManager : [],
      }))
      .sort((a, b) => b.total - a.total);
    return { dateKeys, rows };
  }, [transitions]);

  const toggleTransitionStageExpand = useCallback((stageId) => {
    setExpandedTransitionStages((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) next.delete(stageId);
      else next.add(stageId);
      return next;
    });
  }, []);

  return (
    <PageReveal ready={ready}>
      <main className="chat" id="reportsScreen">
        <div className="crmStickyStack">
          <div className="reportsTabs">
            <button
              type="button"
              className={`reportsTab ${tab === "stats" ? "active" : ""}`}
              data-tab="stats"
              onClick={() => setTab("stats")}
            >
              Статистика
            </button>
            <button
              type="button"
              className={`reportsTab ${tab === "transitions" ? "active" : ""}`}
              onClick={() => setTab("transitions")}
            >
              Переходы
            </button>
            <button
              type="button"
              className={`reportsTab ${tab === "calls" ? "active" : ""}`}
              onClick={() => setTab("calls")}
            >
              Звонки
            </button>
          </div>
          <div className="reportsPeriodRow">
            <input
              type="date"
              className="reportsPeriodDate input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo}
            />
            <span className="reportsPeriodSep">—</span>
            <input
              type="date"
              className="reportsPeriodDate input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom}
            />
          </div>
        </div>

        <div className="section">
          <div className="crmListSurface" id="list">
        <div className={`reportsContent ${tab === "stats" ? "" : "hidden"}`} id="statsTab">
          <div className="section">
            <div>
              <div className="crmGroupHeader">Сводка</div>
              <div className="reportKpiGrid reportKpiGridMain">
                <button
                  type="button"
                  className="reportKpiCard reportKpiCardClickable"
                  onClick={() => openSummaryModal("total_clients")}
                >
                  <div className="reportKpiLabel">Клиентов всего</div>
                  <div className="reportKpiValue">{stats ? summary.total_clients ?? 0 : "—"}</div>
                  <div className="reportKpiMeta">в базе · нажмите для списка</div>
                </button>
                <button
                  type="button"
                  className="reportKpiCard reportKpiCardClickable"
                  onClick={() => openSummaryModal("selection")}
                >
                  <div className="reportKpiLabel">Подборы</div>
                  <div className="reportKpiValue">{stats ? summary.selection_deals ?? 0 : "—"}</div>
                  <div className="reportKpiMeta">воронка «Подбор»</div>
                </button>
                <button
                  type="button"
                  className="reportKpiCard reportKpiCardClickable"
                  onClick={() => openSummaryModal("prepayment")}
                >
                  <div className="reportKpiLabel">Предоплаты</div>
                  <div className="reportKpiValue">{stats ? summary.prepayment_deals ?? 0 : "—"}</div>
                  <div className="reportKpiMeta">этап «Предоплата получена»</div>
                </button>
                <button
                  type="button"
                  className="reportKpiCard reportKpiCardClickable"
                  onClick={() => openSummaryModal("refusal")}
                >
                  <div className="reportKpiLabel">Отказы</div>
                  <div className="reportKpiValue">{stats ? summary.refusal_deals ?? 0 : "—"}</div>
                  <div className="reportKpiMeta">воронка «База отказов»</div>
                </button>
              </div>
              <div className="reportKpiGrid reportKpiGridSecondary">
                <div className="reportKpiCard">
                  <div className="reportKpiLabel">Новых клиентов</div>
                  <div className="reportKpiValue">{stats ? stats.new_users || 0 : "—"}</div>
                  <div className="reportKpiMeta">за период</div>
                </div>
                <div className="reportKpiCard">
                  <div className="reportKpiLabel">Менеджеров</div>
                  <div className="reportKpiValue">{stats ? (stats.by_manager || []).length : "—"}</div>
                  <div className="reportKpiMeta">в отчёте</div>
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <div>
              <div className="crmGroupHeader">По менеджерам</div>
              {byManagerRows.length ? (
                <div className="reportTableWrap">
                  <table className="reportDataTable">
                    <thead>
                      <tr>
                        <th scope="col">Менеджер</th>
                        <th scope="col" className="reportDataTableNum">
                          Всего клиентов
                        </th>
                        <th scope="col" className="reportDataTableNum">
                          Новых за период
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {byManagerRows.map((row) => (
                        <tr key={String(row.id)}>
                          <td className="reportDataTableName">{row.name}</td>
                          <td className="reportDataTableNum">{row.total}</td>
                          <td className="reportDataTableNum">{row.newCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: "10px 12px" }}>
                  <div className="pill">Нет менеджеров</div>
                </div>
              )}
            </div>
          </div>
          <div className="reportDownloadBottom">
            <button
              type="button"
              className="reportDownloadBtnFull"
              onClick={() => downloadReport("stats")}
              disabled={!!downloadingReport}
            >
              {downloadingReport === "stats" ? "Скачивание…" : "Скачать отчёт"}
            </button>
          </div>
        </div>

        <div className={`reportsContent ${tab === "transitions" ? "" : "hidden"}`} id="transitionsTab">
          <div className="section">
            <div>
              <div className="crmGroupHeader">Переходы по этапам</div>
              <div className="crmKvRow">
                <div className="crmKvLabel">Воронка</div>
                <div className="crmKvValue" style={{ maxWidth: 220 }}>
                  <select className="input" value={funnelId} onChange={(e) => setFunnelId(e.target.value)}>
                    {(funnels.length ? funnels : [{ id: funnelId, title: "—" }]).map((f) => (
                      <option key={f.id} value={f.id}>{f.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              {transitionsTable.dateKeys.length && transitionsTable.rows.length ? (
                <div className="reportTableWrap reportTableWrapScroll reportTransitionsMatrixWrap">
                  <table className="reportDataTable reportDataTableMatrix">
                    <thead>
                      <tr>
                        <th scope="col" className="reportDataTableStageCol">
                          Этап
                        </th>
                        <th scope="col" className="reportDataTableNum">
                          Σ
                        </th>
                        {transitionsTable.dateKeys.map((dk) => (
                          <th key={dk} scope="col" className="reportDataTableNum reportDataTableDateHead" title={dk}>
                            {fmtDayShort(dk)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transitionsTable.rows.map((row) => {
                        const hasManagers = row.byManager.length > 0;
                        const open = expandedTransitionStages.has(row.stageId);
                        const mgrKey = (m, i) =>
                          m.managerId != null ? `m-${m.managerId}` : `n-${i}`;
                        return (
                          <Fragment key={row.stageId}>
                            <tr className="reportTransitionStageRow">
                              <td className="reportDataTableName reportDataTableStageCol">
                                <div className="reportTransitionStageCellInner">
                                  {hasManagers ? (
                                    <button
                                      type="button"
                                      className="reportTransitionExpandBtn"
                                      aria-expanded={open}
                                      aria-label={open ? "Свернуть по менеджерам" : "Развернуть по менеджерам"}
                                      onClick={() => toggleTransitionStageExpand(row.stageId)}
                                    >
                                      <span className="reportTransitionExpandIcon" data-open={open ? "1" : ""}>
                                        ▶
                                      </span>
                                    </button>
                                  ) : (
                                    <span className="reportTransitionExpandSpacer" aria-hidden />
                                  )}
                                  <span className="reportTransitionStageTitle">{row.stageTitle}</span>
                                </div>
                              </td>
                              <td className="reportDataTableNum reportDataTableStrong">{row.total}</td>
                              {transitionsTable.dateKeys.map((dk) => (
                                <td key={dk} className="reportDataTableNum">
                                  {Number(row.dates?.[dk]) || 0}
                                </td>
                              ))}
                            </tr>
                            {open &&
                              hasManagers &&
                              row.byManager.map((m, i) => (
                                <tr
                                  key={mgrKey(m, i)}
                                  className="reportTransitionManagerRow"
                                >
                                  <td className="reportDataTableName reportDataTableStageCol reportTransitionManagerCell">
                                    <div className="reportTransitionManagerCellInner">
                                      <span className="reportTransitionManagerIndent" aria-hidden />
                                      <span className="reportTransitionManagerLabel">{m.label || "—"}</span>
                                      {m.roleTag ? (
                                        <span className="reportTransitionManagerRoleTag">{m.roleTag}</span>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td className="reportDataTableNum">{Number(m.total) || 0}</td>
                                  {transitionsTable.dateKeys.map((dk) => (
                                    <td key={dk} className="reportDataTableNum">
                                      {Number(m.dates?.[dk]) || 0}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: "10px 12px" }}>
                  <div className="pill">Нет данных по переходам за период</div>
                </div>
              )}
            </div>
          </div>
          <div className="reportDownloadBottom">
            <button
              type="button"
              className="reportDownloadBtnFull"
              onClick={() => downloadReport("transitions")}
              disabled={!!downloadingReport}
            >
              {downloadingReport === "transitions" ? "Скачивание…" : "Скачать отчёт"}
            </button>
          </div>
        </div>

        <div className={`reportsContent ${tab === "calls" ? "" : "hidden"}`} id="callsTab">
          <div className="section">
            <div>
              <div className="crmGroupHeader">Звонки по менеджерам</div>
              {callsPeriod ? <div className="reportsCallsPeriod">{callsPeriod}</div> : null}
              {callsTotalsResolved && Number(callsTotalsResolved.calls_count) > 0 ? (
                <div className="reportCallsSummary">
                  <div className="reportCallsSummaryLabel">Всего за период</div>
                  <div className="reportCallsSummaryValue">
                    {Number(callsTotalsResolved.calls_count) || 0} звонков · в{" "}
                    {Number(callsTotalsResolved.deals_count) || 0} сделках
                  </div>
                </div>
              ) : null}
              <div id="callsByManager">
                {(calls || []).length ? (
                  (calls || []).map((m) => (
                    <CrmRow
                      key={`call-${m.managerId === null || m.managerId === undefined ? "none" : m.managerId}`}
                      barClass="good"
                      title={m.managerName || "—"}
                      right={`${m.callsCount ?? 0} звонков`}
                      bottom={
                        (m.byDeal || []).length ? (
                          <div className="reportCallsByDealList">
                            {(m.byDeal || []).map((d, i) => (
                              <div
                                key={`${m.managerId}-${d.dealId ?? `n${i}`}`}
                                className="reportCallsByDealRow"
                              >
                                <span className="reportCallsByDealLabel">{d.dealLabel || "—"}</span>
                                <span className="reportCallsByDealCount">{Number(d.callsCount) || 0} зв.</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="crmMeta">Нет разбивки по сделкам</span>
                        )
                      }
                      arrow={false}
                      style={{ cursor: "default" }}
                    />
                  ))
                ) : (
                  <div className="pill">Нет звонков за выбранный период</div>
                )}
              </div>
            </div>
          </div>
          <div className="reportDownloadBottom">
            <button
              type="button"
              className="reportDownloadBtnFull"
              onClick={() => downloadReport("calls")}
              disabled={!!downloadingReport}
            >
              {downloadingReport === "calls" ? "Скачивание…" : "Скачать отчёт"}
            </button>
          </div>
        </div>

          </div>
        </div>
      </main>

      {summaryModalKind ? (
        <div
          className="reportSummaryModalBackdrop"
          role="presentation"
          onClick={closeSummaryModal}
        >
          <div
            className="reportSummaryModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reportSummaryModalTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="reportSummaryModalHeader">
              <h2 className="reportSummaryModalTitle" id="reportSummaryModalTitle">
                {SUMMARY_KINDS[summaryModalKind]?.title || "Детали"}
              </h2>
              <button
                type="button"
                className="reportSummaryModalClose"
                aria-label="Закрыть"
                onClick={closeSummaryModal}
              >
                ×
              </button>
            </div>
            <p className="reportSummaryModalSubtitle">
              {SUMMARY_KINDS[summaryModalKind]?.subtitle || ""}
              {summaryModalTotal > 0 ? ` · записей: ${summaryModalTotal}` : null}
            </p>
            <div className="reportSummaryModalBody">
              {summaryModalLoading && summaryModalItems.length === 0 ? (
                <div className="reportSummaryModalEmpty">Загрузка…</div>
              ) : null}
              {!summaryModalLoading && summaryModalItems.length === 0 ? (
                <div className="reportSummaryModalEmpty">Нет записей</div>
              ) : null}
              <ul className="reportSummaryList">
                {summaryModalItems.map((row, idx) => {
                  if (row.type === "client" && row.user) {
                    const u = row.user;
                    const title = userTitle({ name: u.name, username: null, id: u.telegram_id });
                    return (
                      <li className="reportSummaryRow" key={`c-${u.id}-${idx}`}>
                        <div className="reportSummaryRowMain">
                          <span className="reportSummaryRowTitle">{title}</span>
                          {u.phone ? <span className="reportSummaryRowMeta">{u.phone}</span> : null}
                          <span className="reportSummaryRowMeta">tg: {u.telegram_id}</span>
                          {u.first_contact_at ? (
                            <span className="reportSummaryRowMeta">первый контакт: {fmtDt(u.first_contact_at)}</span>
                          ) : null}
                          {row.manager ? <span className="reportSummaryRowMeta">менеджер: {row.manager}</span> : null}
                        </div>
                        <div className="reportSummaryRowActions">
                          {u.id > 0 ? (
                            <button
                              type="button"
                              className="linkBtn reportSummaryRowBtn"
                              onClick={() => navigate(getChatPath(u.id, "/reports"))}
                            >
                              Чат
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  }
                  if (row.type === "deal" && row.deal && row.user) {
                    const d = row.deal;
                    const u = row.user;
                    const title = userTitle({ name: u.name, username: null, id: u.telegram_id });
                    return (
                      <li className="reportSummaryRow" key={`d-${d.id}-${idx}`}>
                        <div className="reportSummaryRowMain">
                          <span className="reportSummaryRowTitle">
                            Сделка #{d.id} · {d.funnel_title || d.funnel_id}
                          </span>
                          <span className="reportSummaryRowMeta">
                            {d.stage_title || d.stage_id}
                          </span>
                          <span className="reportSummaryRowMeta">клиент: {title}</span>
                          {u.phone ? <span className="reportSummaryRowMeta">{u.phone}</span> : null}
                          {d.refusal_reason ? (
                            <span className="reportSummaryRowMeta">причина: {d.refusal_reason}</span>
                          ) : null}
                          {d.created_at ? (
                            <span className="reportSummaryRowMeta">создана: {fmtDt(d.created_at)}</span>
                          ) : null}
                          {row.manager ? <span className="reportSummaryRowMeta">менеджер: {row.manager}</span> : null}
                        </div>
                        <div className="reportSummaryRowActions">
                          <button
                            type="button"
                            className="linkBtn reportSummaryRowBtn"
                            onClick={() => navigate(`/deals/${d.id}`)}
                          >
                            Карточка
                          </button>
                          {u.id > 0 ? (
                            <button
                              type="button"
                              className="linkBtn reportSummaryRowBtn"
                              onClick={() => navigate(getChatPath(u.id, "/reports"))}
                            >
                              Чат
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
              {summaryModalOffset < summaryModalTotal ? (
                <div className="reportSummaryLoadMore">
                  <button
                    type="button"
                    className="linkBtn"
                    onClick={loadMoreSummaryModal}
                    disabled={summaryModalLoading}
                  >
                    {summaryModalLoading ? "Загрузка…" : "Показать ещё"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </PageReveal>
  );
}

